import { Component, ojs, app, state, v } from "modular-openscriptjs";
import {
    pageInfo,
    scrollToTop,
    either,
    isMobile,
    cleanDispose,
    monetary,
    consoleErrorOnLocal,
} from "../../../helpers";
import { api, dc } from "../../../declarations";
import { routes } from "../../../backendRoutes";
import { tool } from "../../../fotastart";
import SolidIcon from "../../Blocks/SolidIcon";
import OffcanvasBody from "../../Offcanvas/OffcanvasBody";
import ModalBody from "../../Modals/ModalBody";
import Spinner from "../../Blocks/Spinner";
import { Chart, registerables } from "chart.js";

Chart.register(...registerables);

const h = app("h");
const router = app("router");

ojs(SolidIcon, OffcanvasBody, ModalBody, Spinner);

const getPastDate = (daysAgo) => {
    const d = new Date();
    d.setDate(d.getDate() - daysAgo);
    return d.toISOString().split("T")[0];
};

const getTodayDate = () => new Date().toISOString().split("T")[0];

export default class DealReports extends Component {
    companyId = null;

    // List of deals, used for the deal picker and for looking up the
    // currently-reported deal's name. Duplicated here since this page now
    // lives separately from ViewDeals.
    deals = state([]);
    loadingDeals = state(true);

    reportDealId = state(null);
    reportPerformance = state(null);
    reportLoading = state(false);
    reportStartDate = state(getPastDate(6));
    reportEndDate = state(getTodayDate());
    reportFilterHolder = null;
    tempReportStartDate = state(getPastDate(6));
    tempReportEndDate = state(getTodayDate());

    // "day" | "week" | "month" | "year" | "hour" | "minute" - mirrors
    // BusinessAnalytics.js's interval filter exactly, including the
    // client-side availability computation so the dropdown only ever
    // offers options the backend will actually accept for the chosen range.
    reportInterval = state("day");
    tempReportInterval = state("day");
    availableIntervals = state(["day"]);

    quantityChart = null;
    revenueChart = null;
    ordersChart = null;

    constructor() {
        super();

        // Rebuild the report charts whenever fresh performance data arrives.
        this.reportPerformance.listener((val) => {
            const perf = val.value;
            if (perf) {
                setTimeout(() => {
                    this.initQuantityChart(
                        perf.quantity_trend_labels,
                        perf.quantity_trend,
                    );
                    this.initRevenueChart(
                        perf.revenue_trend_labels,
                        perf.revenue_trend,
                    );
                    this.initOrdersChart(
                        perf.orders_trend_labels,
                        perf.orders_trend,
                    );
                }, 50);
            }
        });
    }

    render() {
        this.companyId = router.params.companyId;
        const businessName = window.currentCompany?.name || "Your Business";

        pageInfo(`Deal Reports - ${businessName}`);
        scrollToTop();

        if (this.deals.value.length === 0 && this.loadingDeals.value) {
            this.fetchDeals();
        }

        // Reached via "business.deals.reports.deal" - load that deal's
        // report directly, skipping the picker. Comparing against the
        // current state (rather than a one-shot flag) also lets a link to
        // a *different* deal id re-trigger the load.
        const routeDealId = router.params.dealId || null;
        if (routeDealId && `${this.reportDealId.value}` !== `${routeDealId}`) {
            this.reportDealId.value = routeDealId;
            this.reportPerformance.value = null;
            this.fetchReport();
        }

        return h.div(
            { class: "min-vh-100 py-5 mb-5 py-md-5" },
            h.div(
                { class: "container" },
                // Header
                h.div(
                    { class: "d-flex align-items-center gap-3 mb-4" },
                    h.button(
                        {
                            class: "btn btn-white border shadow-sm rounded-circle p-2",
                            listeners: { click: () => history.back() },
                        },
                        h.SolidIcon("arrow-left"),
                    ),
                    h.div(
                        {},
                        h.h2({ class: "fw-bold mb-0" }, "Deal Reports"),
                        h.p(
                            { class: "text-muted mb-0 small" },
                            "See performance, trends, and top products for a deal.",
                        ),
                    ),
                ),

                this.renderReportSection(),
            ),
        );
    }

    // =====================================================================
    // REPORT
    // =====================================================================

    renderReportSection() {
        return v(this.reportDealId, (dealIdState) =>
            dealIdState.value
                ? this.renderDealReport()
                : this.renderDealPicker(),
        );
    }

    renderDealPicker() {
        return h.div(
            { class: "card border-0 shadow-sm rounded-4" },
            h.div(
                { class: "card-body p-4" },
                h.h5(
                    { class: "fw-bold mb-3" },
                    "Select a deal to view its report",
                ),
                v(this.loadingDeals, (loadingState) => {
                    if (loadingState.value) {
                        return h.div(
                            { class: "text-center py-4" },
                            h.Spinner(),
                        );
                    }

                    return v(this.deals, (dealsState) => {
                        if (dealsState.value.length === 0) {
                            return h.div(
                                { class: "text-muted text-center py-4" },
                                "No deals available yet.",
                            );
                        }

                        return h.div(
                            { class: "list-group list-group-flush" },
                            ...dealsState.value.map((deal) =>
                                h.button(
                                    {
                                        class: "list-group-item list-group-item-action d-flex justify-content-between align-items-center py-3",
                                        listeners: {
                                            click: () =>
                                                this.selectDeal(deal.id),
                                        },
                                    },
                                    h.div(
                                        {},
                                        h.div(
                                            { class: "fw-bold" },
                                            deal.name,
                                        ),
                                        h.div(
                                            { class: "text-muted small" },
                                            this.formatDealValue(deal),
                                        ),
                                    ),
                                    h.i({
                                        class: "bi bi-chevron-right text-muted",
                                    }),
                                ),
                            ),
                        );
                    });
                }),
            ),
        );
    }

    renderDealReport() {
        return h.div(
            {},
            h.div(
                {
                    class: "d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4",
                },
                h.div(
                    { class: "d-flex align-items-center gap-3" },
                    h.button(
                        {
                            class: "btn btn-white border shadow-sm rounded-circle p-2",
                            title: "Change Deal",
                            listeners: {
                                click: () => this.changeDeal(),
                            },
                        },
                        h.SolidIcon("arrow-left"),
                    ),
                    v(this.deals, (dealsState) => {
                        const deal = dealsState.value.find(
                            (d) => d.id == this.reportDealId.value,
                        );
                        return h.h4(
                            { class: "fw-bold mb-0" },
                            deal ? deal.name : "Deal Report",
                        );
                    }),
                ),
                h.button(
                    {
                        class: "btn btn-white border shadow-sm d-flex align-items-center gap-2 rounded-4 px-3 py-2",
                        listeners: {
                            click: () => this.openReportFilterForm(),
                        },
                    },
                    h.i({ class: "bi bi-sliders fs-5 text-secondary" }),
                    h.span({ class: "fw-bold small" }, "Filter"),
                ),
            ),

            // Active filter badges - same pattern as BusinessAnalytics.js:
            // both badges just reopen the filter form, where the actual
            // interval choice happens via the dropdown.
            h.div(
                { class: "mb-4 d-flex flex-wrap align-items-center gap-2" },
                v(this.reportStartDate, () =>
                    h.span(
                        {
                            class: "badge rounded-pill text-dark bg-light border px-3 py-2 fw-normal",
                            role: "button",
                            listeners: {
                                click: () => this.openReportFilterForm(),
                            },
                        },
                        `${this.reportStartDate.value} → ${this.reportEndDate.value}`,
                    ),
                ),
                v(this.reportInterval, () =>
                    h.span(
                        {
                            class: "badge rounded-pill text-dark bg-light border px-3 py-2 fw-normal text-uppercase",
                            role: "button",
                            listeners: {
                                click: () => this.openReportFilterForm(),
                            },
                        },
                        `Per: ${this.reportInterval.value}`,
                    ),
                ),
            ),

            v(this.reportLoading, (loadingState) => {
                if (loadingState.value) {
                    return h.div(
                        { class: "text-center py-5 my-5" },
                        h.Spinner(),
                        h.p(
                            { class: "text-muted mt-3" },
                            "Loading deal performance...",
                        ),
                    );
                }

                return v(this.reportPerformance, (perfState) => {
                    const perf = perfState.value;

                    if (!perf) {
                        return h.div(
                            { class: "text-center py-5 text-muted" },
                            "No performance data available.",
                        );
                    }

                    return h.div(
                        {},
                        h.div(
                            { class: "row g-3 mb-4" },
                            this.renderStatCard(
                                "Completed Orders",
                                `${perf.total_orders}`,
                                "cart-check",
                                "primary",
                            ),
                            this.renderStatCard(
                                "Quantity Sold",
                                `${perf.total_quantity_sold}`,
                                "box-seam",
                                "info",
                            ),
                            this.renderStatCard(
                                "Total Revenue",
                                monetary(perf.total_revenue, dc.userCurrency),
                                "cash-stack",
                                "success",
                            ),
                        ),

                        h.div(
                            { class: "row g-4 mb-4" },
                            this.renderChartCard(
                                "Quantity Sold Trend",
                                "deal-quantity-chart-canvas",
                            ),
                            this.renderChartCard(
                                "Revenue Trend",
                                "deal-revenue-chart-canvas",
                            ),
                        ),

                        h.div(
                            { class: "row g-4 mb-4" },
                            this.renderChartCard(
                                "Orders Trend",
                                "deal-orders-chart-canvas",
                            ),
                        ),

                        this.renderTopProductsCard(perf.top_products || []),
                    );
                });
            }),
        );
    }

    renderStatCard(title, value, icon, color) {
        return h.div(
            { class: "col-12 col-md-4" },
            h.div(
                { class: "card border-0 shadow-sm rounded-4 h-100" },
                h.div(
                    { class: "card-body p-3 p-md-4" },
                    h.div(
                        {
                            class: `bg-${color}-subtle text-${color} rounded-3 d-inline-flex p-2 mb-3`,
                        },
                        h.i({ class: `bi bi-${icon} fs-4` }),
                    ),
                    h.h6(
                        {
                            class: "text-muted small text-uppercase fw-bold mb-1",
                        },
                        title,
                    ),
                    h.h3({ class: "fw-bold mb-0" }, value),
                ),
            ),
        );
    }

    renderChartCard(title, canvasId) {
        return h.div(
            { class: "col-12 col-lg-6" },
            h.div(
                { class: "card border-0 shadow-sm rounded-4 h-100" },
                h.div(
                    { class: "card-body p-4" },
                    h.h5({ class: "fw-bold mb-4" }, title),
                    h.div(
                        { style: "position: relative; height: 280px;" },
                        h.canvas({ id: canvasId }),
                    ),
                ),
            ),
        );
    }

    renderTopProductsCard(topProducts) {
        return h.div(
            { class: "card border-0 shadow-sm rounded-4" },
            h.div(
                { class: "card-body p-4" },
                h.h5(
                    { class: "fw-bold mb-4" },
                    "Top 10 Products in this Deal",
                ),
                topProducts.length === 0
                    ? h.div(
                          { class: "text-center py-5 text-muted" },
                          "No product sales in this period.",
                      )
                    : h.div(
                          { class: "table-responsive" },
                          h.table(
                              { class: "table align-middle mb-0" },
                              h.thead(
                                  h.tr(
                                      {
                                          class: "text-muted small text-uppercase",
                                      },
                                      h.th(
                                          {
                                              class: "fw-bold border-0",
                                              style: "width: 48px;",
                                          },
                                          "#",
                                      ),
                                      h.th(
                                          { class: "fw-bold border-0" },
                                          "Product",
                                      ),
                                      h.th(
                                          {
                                              class: "fw-bold border-0 text-end",
                                          },
                                          "Quantity Sold",
                                      ),
                                      h.th(
                                          {
                                              class: "fw-bold border-0 text-end",
                                          },
                                          "Revenue",
                                      ),
                                  ),
                              ),
                              h.tbody(
                                  ...topProducts.map((product, index) =>
                                      this.renderTopProductRow(
                                          product,
                                          index,
                                      ),
                                  ),
                              ),
                          ),
                      ),
            ),
        );
    }

    renderTopProductRow(product, index) {
        return h.tr(
            h.td({ class: "text-muted fw-bold" }, `${index + 1}`),
            h.td(
                h.div(
                    { class: "d-flex align-items-center gap-3" },
                    either(
                        product.thumbnail,
                        h.img({
                            src: product.thumbnail,
                            class: "rounded-3 object-fit-cover flex-shrink-0",
                            style: "width: 40px; height: 40px;",
                        }),
                        h.div(
                            {
                                class: "bg-light rounded-3 d-flex align-items-center justify-content-center flex-shrink-0",
                                style: "width: 40px; height: 40px;",
                            },
                            h.i({ class: "bi bi-box2 text-muted" }),
                        ),
                    ),
                    h.span({ class: "fw-bold" }, product.name),
                ),
            ),
            h.td({ class: "text-end" }, `${product.quantity_sold}`),
            h.td(
                { class: "text-end fw-bold" },
                monetary(product.revenue, dc.userCurrency),
            ),
        );
    }

    openReportFilterForm() {
        this.tempReportStartDate.value = this.reportStartDate.value;
        this.tempReportEndDate.value = this.reportEndDate.value;
        this.tempReportInterval.value = this.reportInterval.value;
        this.availableIntervals.value = this.getAvailableIntervals(
            this.reportStartDate.value,
            this.reportEndDate.value,
        );

        if (this.reportFilterHolder) cleanDispose(this.reportFilterHolder);

        const form = () => this.renderReportFilterForm();

        if (isMobile()) {
            this.reportFilterHolder = tool.offcanvas(h.OffcanvasBody(form()), {
                class: "rounded-top-5 shadow-lg",
                style: "height: auto; max-height: 85vh;",
            });
        } else {
            this.reportFilterHolder = tool.plainModal(h.ModalBody(form()));
        }

        this.reportFilterHolder.show();
    }

    renderReportFilterForm() {
        return h.div(
            { class: "p-3" },
            h.div(
                {
                    class: "d-flex justify-content-between align-items-center mb-4",
                },
                h.h4({ class: "fw-bold mb-0" }, "Filter Report"),
                h.button({
                    class: "btn-close",
                    listeners: {
                        click: () => this.reportFilterHolder.hide(),
                    },
                }),
            ),

            h.div(
                { class: "d-grid gap-4" },
                h.div(
                    { class: "d-flex flex-column gap-2" },
                    h.label(
                        { class: "text-muted small fw-bold text-uppercase" },
                        "Start Date",
                    ),
                    h.input({
                        type: "date",
                        class: "form-control border shadow-none rounded-3",
                        value: this.tempReportStartDate.value,
                        listeners: {
                            change: (e) =>
                                this.handleTempReportDateChange(
                                    e.target,
                                    "start",
                                ),
                        },
                    }),
                ),

                h.div(
                    { class: "d-flex flex-column gap-2" },
                    h.label(
                        { class: "text-muted small fw-bold text-uppercase" },
                        "End Date",
                    ),
                    h.input({
                        type: "date",
                        class: "form-control border shadow-none rounded-3",
                        value: this.tempReportEndDate.value,
                        listeners: {
                            change: (e) =>
                                this.handleTempReportDateChange(
                                    e.target,
                                    "end",
                                ),
                        },
                    }),
                ),

                // Interval select with dynamic options - identical pattern
                // to BusinessAnalytics.js's own interval dropdown.
                h.div(
                    { class: "d-flex flex-column gap-2" },
                    h.label(
                        { class: "text-muted small fw-bold text-uppercase" },
                        "Interval",
                    ),
                    v(this.availableIntervals, (avail) => {
                        return h.select(
                            {
                                class: "form-select border shadow-none rounded-3",
                                listeners: {
                                    change: (e) => {
                                        this.tempReportInterval.value =
                                            e.target.value;
                                    },
                                },
                            },
                            ...avail.value.map((opt) =>
                                h.option(
                                    { value: opt },
                                    either(
                                        this.tempReportInterval.value === opt,
                                        { selected: true },
                                    ),
                                    opt.toUpperCase(),
                                ),
                            ),
                        );
                    }),
                ),

                h.button(
                    {
                        class: "btn btn-primary rounded-pill p-3 fw-bold shadow-sm mt-2",
                        listeners: {
                            click: () => {
                                this.reportStartDate.value =
                                    this.tempReportStartDate.value;
                                this.reportEndDate.value =
                                    this.tempReportEndDate.value;
                                this.reportInterval.value =
                                    this.tempReportInterval.value;
                                this.reportFilterHolder.hide();
                                this.fetchReport();
                            },
                        },
                    },
                    "Apply Filters",
                ),

                h.button(
                    {
                        class: "btn btn-light rounded-pill p-3 fw-bold",
                        listeners: {
                            click: () => {
                                this.reportStartDate.value = getPastDate(6);
                                this.reportEndDate.value = getTodayDate();
                                this.reportInterval.value = "day";
                                this.reportFilterHolder.hide();
                                this.fetchReport();
                            },
                        },
                    },
                    "Reset All",
                ),
            ),
        );
    }

    handleTempReportDateChange(elem, type) {
        if (type === "start") {
            this.tempReportStartDate.value = elem.value;
        } else {
            this.tempReportEndDate.value = elem.value;
        }

        const available = this.getAvailableIntervals(
            this.tempReportStartDate.value,
            this.tempReportEndDate.value,
        );
        this.availableIntervals.value = available;
        if (!available.includes(this.tempReportInterval.value)) {
            this.tempReportInterval.value = available[0];
        }
    }

    // Mirrors AnalyticsController's getValidIntervals() /
    // BusinessAnalytics.js's getAvailableIntervals() exactly, so the
    // dropdown never offers an interval the backend would reject/clamp.
    getAvailableIntervals(startStr, endStr) {
        const start = new Date(startStr);
        const end = new Date(endStr);
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));

        if (diffDays > 365) {
            return ["year", "month"];
        } else if (diffDays >= 180) {
            return ["month", "week"];
        } else if (diffDays >= 30) {
            return ["month", "week", "day"];
        } else if (diffDays >= 7) {
            return ["week", "day"];
        } else if (diffDays >= 1) {
            return ["day"];
        } else if (diffHours >= 2) {
            return ["hour"];
        } else {
            return ["minute"];
        }
    }

    // =====================================================================
    // NAVIGATION HELPERS
    // =====================================================================

    selectDeal(dealId) {
        this.reportDealId.value = dealId;
        this.reportPerformance.value = null;
        this.fetchReport();
        // Reflect the choice in the URL without a full route re-resolve
        // when we were on the paramless picker route.
        if (router.is("business.deals.reports")) {
            router.to("business.deals.reports.deal", {
                companyId: this.companyId,
                dealId,
            });
        }
    }

    changeDeal() {
        this.reportDealId.value = null;
        this.reportPerformance.value = null;
        if (router.is("business.deals.reports.deal")) {
            router.to("business.deals.reports", {
                companyId: this.companyId,
            });
        }
    }

    // =====================================================================
    // DATA FETCHING
    // =====================================================================

    async fetchDeals() {
        this.loadingDeals.value = true;
        try {
            if (!this.companyId) return;

            const res = await api.get(routes.companyDeals(this.companyId));

            this.loadingDeals.value = false;
            if (res) {
                this.deals.value = res.data.deals || [];
            }
        } catch (e) {
            consoleErrorOnLocal(e);
            this.loadingDeals.value = false;
        }
    }

    async fetchReport() {
        if (!this.companyId || !this.reportDealId.value) return;

        this.reportLoading.value = true;
        try {
            const params = {
                start_date: this.reportStartDate.value,
                end_date: this.reportEndDate.value,
                interval: this.reportInterval.value,
            };

            const res = await api.get(
                routes.companyDealPerformance(
                    this.companyId,
                    this.reportDealId.value,
                ),
                params,
            );

            this.reportLoading.value = false;
            if (res) {
                this.reportPerformance.value = res.data;
                // The backend clamps to a valid interval for the given
                // range - reflect whatever it actually used back into the
                // filter/badge so they never show a stale/invalid choice.
                if (res.data?.selected_interval) {
                    this.reportInterval.value = res.data.selected_interval;
                }
            }
        } catch (e) {
            consoleErrorOnLocal(e);
            this.reportLoading.value = false;
        }
    }

    // =====================================================================
    // HELPERS
    // =====================================================================

    formatDealValue(deal) {
        if (deal.type === "percentage") {
            return `${Number(deal.value)}% off`;
        }
        return `${monetary(deal.value, dc.userCurrency)} off`;
    }

    // =====================================================================
    // CHARTS
    // =====================================================================

    initQuantityChart(labels, data) {
        const ctx = document.getElementById("deal-quantity-chart-canvas");
        if (!ctx) return;

        if (this.quantityChart) this.quantityChart.destroy();

        this.quantityChart = new Chart(ctx, {
            type: "line",
            data: {
                labels: labels,
                datasets: [
                    {
                        label: "Quantity Sold",
                        data: data,
                        borderColor: "#973e87",
                        backgroundColor: "rgba(151, 62, 135, 0.1)",
                        fill: true,
                        tension: 0.4,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: {
                    y: { beginAtZero: true, grid: { display: false } },
                    x: { grid: { display: false } },
                },
            },
        });
    }

    initRevenueChart(labels, data) {
        const ctx = document.getElementById("deal-revenue-chart-canvas");
        if (!ctx) return;

        if (this.revenueChart) this.revenueChart.destroy();

        this.revenueChart = new Chart(ctx, {
            type: "line",
            data: {
                labels: labels,
                datasets: [
                    {
                        label: "Revenue",
                        data: data,
                        borderColor: "#0dcaf0",
                        backgroundColor: "rgba(13, 202, 240, 0.1)",
                        fill: true,
                        tension: 0.4,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: {
                    y: { beginAtZero: true, grid: { display: false } },
                    x: { grid: { display: false } },
                },
            },
        });
    }

    initOrdersChart(labels, data) {
        const ctx = document.getElementById("deal-orders-chart-canvas");
        if (!ctx) return;

        if (this.ordersChart) this.ordersChart.destroy();

        this.ordersChart = new Chart(ctx, {
            type: "line",
            data: {
                labels: labels,
                datasets: [
                    {
                        label: "Orders",
                        data: data,
                        borderColor: "#fed27d",
                        backgroundColor: "rgba(254, 210, 125, 0.15)",
                        fill: true,
                        tension: 0.4,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: {
                    y: { beginAtZero: true, grid: { display: false } },
                    x: { grid: { display: false } },
                },
            },
        });
    }

    cleanUp() {
        if (this.quantityChart) this.quantityChart.destroy();
        if (this.revenueChart) this.revenueChart.destroy();
        if (this.ordersChart) this.ordersChart.destroy();
        if (this.reportFilterHolder) cleanDispose(this.reportFilterHolder);
        super.cleanUp();
    }
}

ojs(DealReports);
